import {Paths, PathValue} from "@2d-front-monorepo/core";
import {ComponentProps, ComponentType, JSX, ReactNode} from "react";
import {TableCellProps} from "./TableCellProps";
import {TableColumnType} from "./TableColumnType";

interface TableColumnBaseProps<T extends object, P extends Paths<T>> {
	align?: "left" | "right" | "center",
	className?: string,
	title: ReactNode | string,
	type?: TableColumnType,
	property: P,
}

interface RenderColumnProps<T extends object, P extends Paths<T>> extends TableColumnBaseProps<T, P> {
	render: (row: T, cell: PathValue<T, P & string>) => ReactNode,
	surrogate?: never,
	cell?: never,
}

interface PropertyColumnProps<T extends object, P extends Paths<T>> extends TableColumnBaseProps<T, P> {
	surrogate?: (value: PathValue<T, P & string>) => ReactNode,
	render?: never,
	cell?: never,
}

/**
 * Cell column. The cell component receives `row` and the resolved `cell` value;
 * any *extra* props it declares (beyond `row`/`cell`) become props of the column
 * itself, so they are supplied — and type-checked — right where the cell is used
 * (e.g. `cell={EditDeleteActions} onEdit={…} onDelete={…}`). No context required.
 *
 * Because TypeScript has no partial type-argument inference, the cell component
 * must be named as the third generic to enable that extra-prop checking:
 * `<TableColumn<Row, "id", typeof EditDeleteActions> property="id" cell={EditDeleteActions} …/>`.
 * Cells with no extra props can omit it and rely on the default.
 */
type ComponentColumnProps<T extends object, P extends Paths<T>, C extends ComponentType<any>> =
	TableColumnBaseProps<T, P>
	& {
	cell: C,
	render?: never,
	surrogate?: never,
}
	& Omit<ComponentProps<C>, keyof TableCellProps<unknown, unknown>>;

export type TableColumnProps<
	T extends object,
	P extends Paths<T> = Paths<T>,
	C extends ComponentType<any> = ComponentType<TableCellProps<T, PathValue<T, P & string>>>
> =
	| RenderColumnProps<T, P>
	| PropertyColumnProps<T, P>
	| ComponentColumnProps<T, P, C>;

export default function TableColumn<
	T extends object,
	P extends Paths<T> = Paths<T>,
	C extends ComponentType<any> = ComponentType<TableCellProps<T, PathValue<T, P & string>>>
>(props: TableColumnProps<T, P, C>): JSX.Element {
	return <></>;
}
